import React, { useLayoutEffect, useRef, useState } from 'react';
import { classNames } from '@vkontakte/vkjs';
import { Touch, TouchProps } from '@vkontakte/vkui';
import { createCompoundComponent } from '@djs/react';
import { CSSTransition } from '@dui/motion';
import { useDUIContext } from '../DUIContext';
import { MessageBubbleTyping } from '../MessageBubbleTyping';
import { MessageListItem } from './MessageListItem';
import { MessagesListSystemLine } from './MessagesListSystemLine';
import styles from './MessagesList.module.css';

const TYPING_APPEARANCE_DURATION = 400;

interface MessagesListProps {
  className?: string;
  children?: React.ReactNode;
  incomingTyping?: boolean;
  typingLabel?: string;
  wrapperTouchProps?: TouchProps;
}

/**
 * Обертка для истории переписки. В качестве children должны
 * быть компоненты MessagesList.Item или MessageList.SystemLine
 */
export const MessagesList = createCompoundComponent(
  function MessagesList(props: MessagesListProps) {
    const { children, className, incomingTyping, wrapperTouchProps, typingLabel } = props;
    const { motionReduced } = useDUIContext();

    const typingRef = useRef<HTMLDivElement>(null);
    const [typingBubbleHeight, setTypingBubbleHeight] = useState(0);

    useLayoutEffect(() => {
      const typingEl = typingRef.current;
      if (!motionReduced && typingEl && !typingBubbleHeight) {
        typingEl.style.display = 'block';
        setTypingBubbleHeight(typingEl.clientHeight);
        typingEl.style.display = '';
      }
    }, []); // eslint-disable-line

    const Wrapper = wrapperTouchProps ? Touch : 'div';

    return (
      <CSSTransition
        on={Boolean(incomingTyping)}
        className={classNames(className, styles.root)}
        style={
          {
            '--typing-appearance-duration': `${TYPING_APPEARANCE_DURATION}ms`,
            '--typing-bubble-height': `${typingBubbleHeight}px`,
          } as React.CSSProperties
        }
        duration={TYPING_APPEARANCE_DURATION}
        classNames={{
          willEnter: styles.typing__willEnter,
          entering: styles.typing__entering,
          entered: '',
          willExit: '',
          exiting: styles.typing__exiting,
          exited: styles.typing__exited,
        }}
      >
        <Wrapper {...wrapperTouchProps} className={styles.content}>
          {children}
        </Wrapper>
        <MessageListItem
          getRootRef={typingRef}
          aria-hidden={!incomingTyping}
          align="incoming"
          className={styles.typingIndicator}
        >
          <MessageBubbleTyping testId="chat-user-typing-bubble" mode="incoming" label={typingLabel} />
        </MessageListItem>
      </CSSTransition>
    );
  },
  { Item: MessageListItem, SystemLine: MessagesListSystemLine },
);
